import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useStreamTokenRenewal } from './useStreamTokenRenewal';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchExternalVideoById,
  getExternalVideoStreamUrl,
  fetchRelatedExternalVideos,
  fetchStreamToken,
  fetchExternalVideoThumbnail
} from '../../services/externalVideoApi/externalVideoApi';
import type { ExternalVideo } from '../../services/externalVideoApi/externalVideoApi.types';
import { getDefaultThumbnailUrl } from '../../utils/thumbnailUtils';
import VideoDetailPlayerSection from '../../components/VideoDetailPlayerSection/VideoDetailPlayerSection';
import VideoDetailInfo from '../../components/VideoDetailInfo/VideoDetailInfo';
import VideoCardSide from '../../components/VideoCardSide/VideoCardSide';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import { VideoData } from '../../components/VideoCardSide/types';
import { FALLBACK_LABELS, EXTERNAL_VIDEO_MESSAGES } from '../../config/constants';

import styles from '../VideoDetail/VideoDetail.module.css';
import extStyles from './ExternalVideoDetail.module.css';
import relatedStyles from '../../components/RelatedVideosList/RelatedVideosList.module.css';

/** プレイヤーRef の型定義 */
interface PlayerRef {
  isPlaying: () => boolean;
  play: () => void;
  pause: () => void;
  seekTo: (time: number, type: string) => void;
  getCurrentTime: () => number;
  setMuted: (muted: boolean) => void;
}

const ExternalVideoDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [video, setVideo] = useState<ExternalVideo | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<VideoData[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [resumePosition, setResumePosition] = useState(0);
  const playerRef = useRef<PlayerRef | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const thumbnailBlobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!id) return;
    const abortController = new AbortController();

    // id が変わった際に旧 blob URL を解放
    const previousBlobUrls = [...thumbnailBlobUrlsRef.current];
    thumbnailBlobUrlsRef.current = [];
    previousBlobUrls.forEach(url => URL.revokeObjectURL(url));

    setError(null);
    setVideo(null);
    setStreamUrl(null);
    setPermissionError(null);
    setRelatedVideos([]);
    setRelatedError(false);
    setRelatedLoading(false);

    const loadStreamToken = async (videoId: string): Promise<void> => {
      try {
        const { token } = await fetchStreamToken(videoId, abortController.signal);
        if (!abortController.signal.aborted) {
          setStreamUrl(getExternalVideoStreamUrl(videoId, token));
        }
      } catch (tokenErr) {
        if (abortController.signal.aborted) return;
        setRelatedError(false);
        if (axios.isAxiosError(tokenErr) && tokenErr.response?.status === 403) {
          setPermissionError('この動画の再生は許可されていません');
        } else {
          setPermissionError('ストリームトークンの取得に失敗しました');
        }
      }
    };

    const loadRelatedVideos = async (videoId: string): Promise<void> => {
      setRelatedLoading(true);
      try {
        const videos = await fetchRelatedExternalVideos(videoId);
        if (abortController.signal.aborted) return;

        const mapped = await Promise.all(
          videos.map(async (v): Promise<VideoData> => {
            let thumbnailUrl: string | undefined;
            try {
              thumbnailUrl = await fetchExternalVideoThumbnail(v.id, abortController.signal);
              thumbnailBlobUrlsRef.current.push(thumbnailUrl);
            } catch (thumbErr) {
              console.warn(`サムネイル取得失敗 (id=${v.id}):`, thumbErr);
              thumbnailUrl = getDefaultThumbnailUrl();
            }
            return {
              id: `ext_${v.id}`,
              external_id: v.id,
              isExternal: true,
              title: v.title || FALLBACK_LABELS.UNTITLED,
              thumbnail: thumbnailUrl,
              uploader: {
                name: v.uploader_name || FALLBACK_LABELS.ANONYMOUS,
                hospital: { name: v.hospital_name || FALLBACK_LABELS.EXTERNAL_HOSPITAL }
              },
              created_at: v.created_at
            };
          })
        );

        if (!abortController.signal.aborted) {
          setRelatedError(false);
          setRelatedVideos(mapped);
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error('関連動画取得エラー:', err);
          setRelatedError(true);
          setRelatedVideos([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setRelatedLoading(false);
        }
      }
    };

    const loadExternalVideo = async (): Promise<void> => {
      try {
        const data = await fetchExternalVideoById(id);
        if (abortController.signal.aborted) return;

        setVideo(data);
        void loadStreamToken(id);
        void loadRelatedVideos(id);
      } catch (fetchErr) {
        if (!abortController.signal.aborted) {
          console.warn('外部動画取得エラー:', fetchErr);
          if (axios.isAxiosError(fetchErr) && fetchErr.response?.status === 404) {
            setError('指定された外部動画が見つかりません');
          } else {
            setError(EXTERNAL_VIDEO_MESSAGES.FETCH_ERROR);
          }
        }
      }
    };

    void loadExternalVideo();

    return () => {
      abortController.abort();
      // アンマウント時に blob URL を解放
      thumbnailBlobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      thumbnailBlobUrlsRef.current = [];
    };
  }, [id]);

  // ストリームトークン自動更新
  const getPlayerTime = useCallback(() => playerRef.current?.getCurrentTime() ?? 0, []);
  useStreamTokenRenewal({
    videoId: id,
    streamUrl,
    onRenewed: useCallback((newUrl: string, savedPos: number) => {
      setResumePosition(savedPos);
      setStreamUrl(newUrl);
    }, []),
    getPlayerTime,
    onPermissionRevoked: useCallback(() => {
      setStreamUrl(null);
      setPermissionError('この動画の再生権限が失われました');
    }, []),
  });

  // プレイヤー制御
  const handleTogglePlay = useCallback(() => {
    if (!playerRef.current) return;
    try {
      if (playerRef.current.isPlaying()) {
        playerRef.current.pause();
      } else {
        playerRef.current.play();
      }
    } catch (e) {
      console.warn('プレイヤー操作エラー (togglePlay):', e);
    }
  }, []);

  const handleSeek = useCallback((time: number) => {
    if (!playerRef.current) return;
    try {
      playerRef.current.seekTo(time, 'seconds');
    } catch (e) {
      console.warn('プレイヤー操作エラー (seek):', e);
    }
  }, []);

  const handleSeekBackward = useCallback(() => {
    if (!playerRef.current) return;
    try {
      const current = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(Math.max(0, current - 10), 'seconds');
    } catch (e) {
      console.warn('プレイヤー操作エラー (seekBackward):', e);
    }
  }, []);

  const handleSeekForward = useCallback(() => {
    if (!playerRef.current) return;
    try {
      const current = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(current + 10, 'seconds');
    } catch (e) {
      console.warn('プレイヤー操作エラー (seekForward):', e);
    }
  }, []);

  const handleToggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      if (playerRef.current && typeof playerRef.current.setMuted === 'function') {
        playerRef.current.setMuted(next);
      }
      return next;
    });
  }, []);

  const handleTagClick = useCallback((tag: string) => {
    navigate(`/?q=${encodeURIComponent(tag)}`);
  }, [navigate]);

  const handleComment = useCallback(() => {}, []);
  const handleCommentWithTime = useCallback(() => {}, []);

  // 無操作ハンドラ（外部動画では使用不可の機能）
  const noop = useCallback(() => {}, []);
  const noopAsync = useCallback(async (_?: unknown) => ({ success: false }), []);

  if (error) return (
    <div className={styles['video-detail-outer-container']}>
      <p>{error}</p>
    </div>
  );
  if (!video) return <LoadingIndicator className={styles['video-detail-loading-indicator']} />;

  const hospitalName = video.hospital_name || FALLBACK_LABELS.EXTERNAL_HOSPITAL;
  const uploaderName = video.uploader_name || FALLBACK_LABELS.ANONYMOUS;
  const uploaderIconUrl = '/asset/no_icon_user.png';
  const showRelatedSidebar = permissionError === null;

  // VideoDetailInfo 用の video オブジェクトを構築
  const videoForInfo = {
    id: undefined as number | undefined,
    title: video.title || FALLBACK_LABELS.UNTITLED,
    description: undefined as string | undefined,
    view_count: 0,
    created_at: video.created_at,
  };

  return (
    <div className={styles['video-detail-outer-container']}>
      <div className={styles['video-detail-main-container']}>
        <div className={styles['video-detail-left-side']}>
          <div className={styles['video-detail-player-container']}>
            {permissionError ? (
              <div className={extStyles['ExternalVideoDetail-permissionContainer']}>
                <div>
                  <p className={extStyles['ExternalVideoDetail-permissionTitle']}>再生が許可されていません</p>
                  <p className={extStyles['ExternalVideoDetail-permissionReason']}>{permissionError}</p>
                </div>
              </div>
            ) : streamUrl ? (
              <VideoDetailPlayerSection
                videoPath={streamUrl}
                videoId={video.id}
                useStreaming={false}
                playerRef={playerRef}
                autoPlay={true}
                startMuted={isMuted}
                initialPosition={resumePosition}
              />
            ) : (
              <div className={extStyles['ExternalVideoDetail-loadingContainer']}>
                <LoadingIndicator />
              </div>
            )}
          </div>

          <div className={styles['video-detail-info-section']}>
            <VideoDetailInfo
              video={videoForInfo}
              uploaderIconUrl={uploaderIconUrl}
              hospitalName={hospitalName}
              uploaderName={uploaderName}
              handleTagClick={handleTagClick}
              disabled={true}
            />
          </div>

          <div className={extStyles['ExternalVideoDetail-featureNotice']}>
            <p className={extStyles['ExternalVideoDetail-featureNoticeText']}>
              いいね・お気に入り・コメントなどの機能は、外部動画でも今後対応予定です。
            </p>
          </div>
        </div>

        {showRelatedSidebar && (
          <div className={styles['video-detail-right-side']}>
            <div className={relatedStyles['related-videos-list-container']}>
              {relatedLoading && (
                <LoadingIndicator
                  align="left"
                  className={relatedStyles['related-videos-list-loading']}
                />
              )}
              {!relatedLoading && (
                <>
                  {relatedVideos.length > 0 ? (
                    <div className={relatedStyles['related-videos-list']}>
                      {relatedVideos.map((rv) => (
                        <VideoCardSide
                          key={rv.id}
                          video={rv}
                          hideVisibilityToggle={true}
                        />
                      ))}
                    </div>
                  ) : relatedError ? (
                    <p>関連動画を読み込めませんでした。</p>
                  ) : (
                    <p>{EXTERNAL_VIDEO_MESSAGES.NO_RELATED_VIDEOS}</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalVideoDetail;
